// src/components/forms/ChangeEmailForm.jsx - FINAL (Proper styling + visible input text)

import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { updateMe } from '@/services/apiService';
import { motion } from 'framer-motion';

function classNames(...xs) {
  return xs.filter(Boolean).join(' ');
}

export const ChangeEmailForm = ({ currentEmail, onEmailChanged }) => {
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // ✅ Handle cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Validation
    if (!newEmail.trim()) {
      setError('Please enter an email address.');
      setLoading(false);
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError('Please enter a different email address.');
      setLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      const form = new FormData();
      form.append('email', newEmail);

      await updateMe(form);
      
      setMessage('✓ Email updated! Verification email sent to your new address.');
      setNewEmail('');
      setCooldownSeconds(60); // Start 60-second cooldown after successful change
      onEmailChanged?.();
    } catch (err) {
      console.error('Change email error:', err);
      
      const status = err?.response?.status;
      
      // ✅ Handle 429 (Too Many Requests) rate limit
      if (status === 429) {
        const retryAfter = err?.response?.data?.retry_after || 60;
        setCooldownSeconds(retryAfter);
        setError(`Please wait ${retryAfter} seconds before changing email again.`);
      } else {
        setError(
          err?.response?.data?.error ||  // ✅ Try backend error message first
          err?.response?.data?.email?.[0] ||
          'Failed to update email. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="rounded-lg border border-zinc-700 bg-transparent p-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h3 className="font-semibold text-swan mb-4">Change Email Address</h3>

      <motion.form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        transition={{ duration: 0.2 }}
      >
        <div>
          <label className="block text-xs uppercase tracking-wide text-mikado-yellow mb-2">
            New Email Address
          </label>
          <input
            type="email"
            placeholder="your-new-email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={loading || cooldownSeconds > 0}
            className={classNames(
              'w-full rounded-md border bg-transparent px-3 py-2 text-swan placeholder-field-grey',
              error
                ? 'border-imperial-red focus:ring-imperial-red'
                : 'border-zinc-700 focus:outline-none focus:ring-1 focus:ring-field-grey'
            )}
          />
        </div>

        {error && (
          <motion.p
            className="text-xs text-imperial-red"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.p>
        )}

        {message && (
          <motion.p
            className="text-xs text-mikado-yellow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {message}
          </motion.p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading || !newEmail.trim() || cooldownSeconds > 0}
            className={classNames(
              'inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium',
              loading || !newEmail.trim() || cooldownSeconds > 0
                ? 'border-zinc-700 text-field-grey opacity-50 cursor-not-allowed'
                : 'border-mikado-yellow text-mikado-yellow hover:bg-yellow-950/20'
            )}
          >
            {cooldownSeconds > 0
              ? `Wait ${cooldownSeconds}s`
              : loading
              ? 'Updating...'
              : 'Update Email'}
          </button>
          <button
            type="button"
            disabled={loading || cooldownSeconds > 0}
            onClick={() => {
              setNewEmail('');
              setError('');
              setMessage('');
            }}
            className="inline-flex items-center justify-center rounded-md border border-zinc-800 px-4 py-2 text-sm hover:bg-zinc-900"
          >
            Cancel
          </button>
        </div>

        <p className="text-xs text-field-grey">
          You'll need to verify this new email address before you can submit to competitions.
        </p>
      </motion.form>
    </motion.div>
  );
};

export default ChangeEmailForm;